import Dinero from 'dinero.js';
import {
  BaseItemRequest,
  CalculateFinancialSummaryRequest,
  CustomProductLineItem,
  CustomRentalItemRequest,
  CustomSaleItemRequest,
  DamageWaiverLineItem,
  DeliveryLineItem,
  FeeLineItem,
  FinancialSummary,
  ItemRequest,
  LineItem,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  RentalItemRequest,
  SaleItemRequest,
  TaxLineItem,
} from '../../../../../clients/quipli-service/financials/financials';
import {
  CreateCustomProductRequest,
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderDocumentType,
  OrderProduct,
  OrderType,
  UpdateOrderFinancialRequest,
} from '../../../../../clients/quipli-service/order/order';
import { dineroSum, safeRound } from '../../../../../util/util';
import { FuelCharge } from '../types/fuel-charge-types';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { ProductType } from '../../../../../clients/quipli-service/product/product';
import { toShippingType } from '../../../../../clients/quipli-service/delivery/delivery';
import { Duration } from 'luxon';
import { Dictionary } from 'lodash';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';

export function offRentCalculations(req: {
  order?: Order;
  currency?: Dinero.Currency;
  hourOverageOverrides: { orderProductId: number; unitId: number; overageCharge: number; taxable: boolean }[];
  inventoryReturnRequest?: InventoryReturnRequest;
  existingSummary?: FinancialSummary;
  lateChargeSummary?: FinancialSummary;
  waiveLateCharge: boolean;
  offRentRequest?: OffRentRequest;
}) {
  const {
    order,
    currency,
    hourOverageOverrides,
    inventoryReturnRequest,
    existingSummary,
    lateChargeSummary,
    waiveLateCharge,
    offRentRequest,
  } = req;

  const hourOverageSubtotal =
    currency &&
    dineroSum(
      hourOverageOverrides
        .filter((ov) => inventoryReturnRequest?.orderProductIds.includes(ov.orderProductId))
        .map((ov) => Dinero({ amount: safeRound(ov.overageCharge ?? 0), currency })),
      currency,
    );
  const hourOverageTax =
    currency &&
    dineroSum(
      hourOverageOverrides
        .filter((ov) => inventoryReturnRequest?.orderProductIds.includes(ov.orderProductId) && ov.taxable)
        .map((ov) => Dinero({ amount: safeRound(ov.overageCharge ?? 0), currency })),
      currency,
    ).multiply(order?.taxPercentage || 0);
  const hourOverageTotal = hourOverageSubtotal && hourOverageTax && hourOverageSubtotal.add(hourOverageTax);
  const adhocLineItems =
    order?.invoices.flatMap((inv) =>
      inv.lineItems.filter((li) => li.type === 'LineItem' || li.type === 'CreditCardFee'),
    ) || [];
  const adHocLineItemsSubTotal =
    currency &&
    dineroSum(
      adhocLineItems.map((li) => Dinero({ amount: safeRound(li.total * 100), currency })),
      currency,
    );
  const adHocLineItemsTax =
    currency &&
    dineroSum(
      adhocLineItems.map((li) => {
        if (li.taxable) {
          if (li.taxOverride) {
            return Dinero({ amount: safeRound(li.taxOverride * 100), currency });
          } else {
            const subtotal = Dinero({ amount: safeRound(li.total * 100), currency });
            const taxRate = order?.taxPercentage || 0;
            return subtotal.multiply(taxRate);
          }
        } else {
          return Dinero({ amount: 0, currency });
        }
      }),
      currency,
    );
  const adHocLineItemsTotal =
    adHocLineItemsSubTotal && adHocLineItemsTax && adHocLineItemsSubTotal.add(adHocLineItemsTax);

  let paymentsTotal: Dinero.Dinero;
  let lateChargeTotal: Dinero.Dinero | undefined;
  let total: Dinero.Dinero | undefined;
  let balance: Dinero.Dinero;
  if (order?.isCycleBilled) {
    lateChargeTotal = total = hourOverageTotal;
    balance = hourOverageTotal || Dinero({ amount: 0, currency });
    paymentsTotal = Dinero({ amount: 0, currency });
  } else {
    lateChargeTotal =
      existingSummary &&
      lateChargeSummary &&
      hourOverageTotal &&
      lateChargeSummary.total.add(hourOverageTotal).subtract(existingSummary.total);
    total =
      hourOverageTotal &&
      adHocLineItemsTotal &&
      lateChargeSummary?.total.add(hourOverageTotal).add(adHocLineItemsTotal);
    if (waiveLateCharge) {
      total =
        adHocLineItemsTotal &&
        existingSummary?.total.add(adHocLineItemsTotal).add(hourOverageTotal ?? Dinero({ amount: 0, currency }));
    }
    paymentsTotal =
      currency && order
        ? dineroSum(
            order.invoices
              .flatMap((inv) => inv.payments)
              .filter((p) => p.status === 'succeeded')
              .flatMap((p) => p.invoicePayments.map((_) => Dinero({ amount: safeRound(_.amount * 100), currency }))),
            currency,
          )
        : Dinero({ amount: 0, currency });
    balance = total ? total.subtract(paymentsTotal) : Dinero({ amount: 0, currency });
  }
  const isValid = offRentRequest && offRentRequest.offRentProducts.length > 0;
  return {
    hourOverageSubtotal,
    hourOverageTax,
    hourOverageTotal,
    adhocLineItems,
    adHocLineItemsSubTotal,
    adHocLineItemsTax,
    adHocLineItemsTotal,
    paymentsTotal,
    lateChargeTotal,
    total,
    balance,
    isValid,
  };
}

export function orderToCalculateFinancialSummaryRequest(
  order: Order,
  customPrices: RentalProductPrice[],
): CalculateFinancialSummaryRequest {
  return {
    customerId: order.user.id,
    locationId: order.locationId,
    deliveryType: order.products[0].shippingType,
    customDelivery:
      order.shippingUpdatedAt && order.shippingCharged !== undefined ? order.shippingCharged * 100 : undefined,
    customPickup: order.pickupUpdatedAt && order.pickupCharged !== undefined ? order.pickupCharged * 100 : undefined,
    customTax: order.taxUpdatedAt && order.taxCharged ? order.taxCharged * 100 : undefined,
    customTaxPercentage: order.taxPercentageUpdatedAt && order.taxPercentage,
    customDamageWaiver:
      order.damageWaiverUpdatedAt && order.damageWaiver !== undefined ? order.damageWaiver * 100 : undefined,
    customFees: order.additionalFees
      .filter((af) => af.updatedAt)
      .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
      .map((fee) => ({
        feeId: fee.feeId,
        amount: (fee.feeAppliedValue || 0) * 100,
      })),
    customProductFees: order.products.flatMap((product) =>
      product.additionalFees
        .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
        .map((fee) => ({
          feeId: fee.attributeId,
          productId: product.product.id,
          productLineItemId: `${product.id}`,
          amount: (fee.feeAppliedValue || 0) * 100,
        })),
    ),
    couponCode: order.discountCoupon,
    additionalDiscount:
      order.additionalDiscount && order.additionalDiscount > 0 ? order.additionalDiscount * 100 : undefined,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    paymentMethod: order.paymentMethodType,
    isCycleBilled: order.isCycleBilled,
    cycleLength: order.cycleBillingTerms?.cycleLength,
    items: order.products.map((op) =>
      orderProductToOrderInputItem(
        { ...op, id: op.id.toString() },
        customPrices.filter(
          (cp) => cp.productId === op.product.id && (cp.orderProductId === op.id || !cp.orderProductId),
        ),
      ),
    ),
    taxRateId: order.taxCode,
    documentType: OrderDocumentType.Order,
  };
}

export function orderProductToOrderInputItem(
  orderProduct: Omit<OrderProduct, 'id'> & { id: string },
  customPrices: RentalProductPrice[],
): ItemRequest {
  const baseInfo: BaseItemRequest = {
    id: orderProduct.id.toString(),
    productId: orderProduct.product.id,
    quantity: orderProduct.quantity,
    manualDiscount: orderProduct.discountPercentage,
  };
  switch (orderProduct.type) {
    case OrderType.Sale: {
      const saleItem: SaleItemRequest | CustomSaleItemRequest = {
        ...baseInfo,
        type: OrderType.Sale,
        services: orderProduct.services.map((service) => {
          return {
            id: service.serviceId,
            value: service.value,
          };
        }),
        ...(orderProduct.product.type === ProductType.Custom && {
          name: orderProduct.name,
          description: orderProduct.description,
          taxable: !orderProduct.isTaxFree,
          customPrice: orderProduct.customerBuyingPrice * 100,
          taxCategoryId: orderProduct.taxCategoryId,
        }),
      };
      return saleItem;
    }
    case OrderType.Rental: {
      const rentalItem: RentalItemRequest | CustomRentalItemRequest = {
        ...baseInfo,
        type: OrderType.Rental,
        start: orderProduct.rentalStartDate,
        end: orderProduct.rentalEndDate,
        customOrderRentalRates: orderProduct.customOrderRentalRates ?? customPrices,
        ...(orderProduct.product.type === ProductType.Custom && {
          name: orderProduct.name,
          description: orderProduct.description,
          taxable: !orderProduct.isTaxFree,
          equipmentOwner: orderProduct.equipmentOwner,
          taxCategoryId: orderProduct.taxCategoryId,
        }),
      };
      return rentalItem;
    }
  }
}

export function toUpdateOrderRequest(
  order: Order,
  customPrices: RentalProductPrice[],
  offRentRequest: OffRentRequest | null,
  summary: FinancialSummary,
  currency: Dinero.Currency,
): UpdateOrderFinancialRequest {
  const cartTotal = summary.lineItems.reduce(
    (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency }),
  );
  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
  const couponCode = order.user.tagPricing?.name || order.discountCoupon;
  const couponDiscount =
    summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
    Dinero({ amount: 0, currency });
  const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
  const existingProductFees = order.products.flatMap((p) => p.additionalFees);
  const discount =
    summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ?? Dinero({ amount: 0, currency });

  const novelProductLineItems = (
    summary.lineItems.filter(
      (li) => li.type === 'Product' && !order.products.some((op) => op.id.toString() === li.id.toString()),
    ) as ProductLineItem[]
  ).map((li) => ({
    productId: li.productId,
    delivery: 2,
    pickup: 2,
    isCustom: false as const,
    type: OrderType.Sale,
    quantity: li.quantity,
    amount: Math.trunc(li.amount.getAmount() / li.quantity),
    services: [],
    options: [],
    hasCustomAmount: true,
  }));

  return {
    locationId: order.locationId,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    deliveryType: order.products[0].shippingType,
    currency: summary.total.getCurrency(),
    cartTotal: cartTotal.getAmount(),
    damageWaiver: damageWaiver
      ? {
          amount: damageWaiver.amount.getAmount(),
          hasCustomAmount: damageWaiver.hasCustomAmount,
        }
      : undefined,
    delivery: delivery
      ? {
          amount: delivery?.getAmount() || 0,
          hasCustomAmount: orderDelivery?.hasCustomAmount || false,
        }
      : undefined,
    pickup: pickup
      ? {
          amount: pickup?.getAmount() || 0,
          hasCustomAmount: orderPickup?.hasCustomAmount || false,
        }
      : undefined,
    tax: {
      taxCode: tax.taxCodeId,
      amount: tax.amount.getAmount(),
      hasCustomAmount: tax.hasCustomAmount,
      hasCustomPercentage: tax.hasCustomPercentage,
      percentage: tax.percentage,
    },
    coupon: couponCode
      ? {
          code: couponCode,
          amount: couponDiscount.multiply(-1).getAmount(),
          isVip: false,
        }
      : undefined,
    documentType: OrderDocumentType.Order,
    additionalFees: locationFees.map((fee) => {
      const orderFee = order.additionalFees.find((f) => f.feeId === fee.feeId);

      return {
        orderFeeId: orderFee?.id,
        feeId: fee.feeId,
        amount: fee.amount.getAmount(),
        hasCustomAmount: fee.hasCustomAmount,
      };
    }),
    additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
    total: summary.total.getAmount(),
    googleCalculation: summary.googleCalculation,
    securityDeposit:
      order.rentalSecurity !== 0
        ? {
            amount: safeRound(order.rentalSecurity * 100),
            percentage: order.rentalSecurityPercentage,
          }
        : undefined,
    products: [
      ...order.products.map((op) => {
        if (op.product.type === ProductType.Custom) {
          const li = summary.lineItems.find(
            (line) => line.type === 'CustomProduct' && line.id === op.id.toString(),
          )! as CustomProductLineItem;
          const customProduct: CreateCustomProductRequest = {
            isCustom: true,
            name: op.name,
            description: op.description,
            productId: op.product.id,
            uniqueId: op.id.toString(),
            type: op.type,
            start: op.type === OrderType.Rental ? op.rentalStartDate : undefined,
            end: op.type === OrderType.Rental ? op.rentalEndDate : undefined,
            quantity: op.quantity,
            taxable: !op.isTaxFree,
            amount: li.unitPrice.getAmount(),
            hasCustomAmount: li.hasCustomAmount,
            taxCategoryId: op.taxCategoryId,
            generalLedgerAccountId: op.generalLedgerAccountId,
            deliveryDriverId: op.deliveryDriverId,
            pickupDriverId: op.pickupDriverId,
            equipmentOwner: op.equipmentOwner,
          };
          return customProduct;
        } else {
          const productLineItem = summary.lineItems.find(
            (line) =>
              ((op.product.type === ProductType.Custom && line.type === 'CustomProduct') || line.type === 'Product') &&
              line.id === op.id.toString(),
          )! as ProductLineItem;

          return {
            uniqueId: op.id.toString(),
            productId: op.product.id,
            type: productLineItem.transactionType,
            amount: productLineItem.unitPrice.getAmount(),
            hasCustomAmount: productLineItem.hasCustomAmount,
            deliveryType: toShippingType(op.shippingTypeId),
            pickup:
              summary.lineItems
                .find((line) => line.type === 'ProductPickup' && line.productLineItemId === productLineItem.id)
                ?.amount?.getAmount() || 0,
            delivery:
              summary.lineItems
                .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === productLineItem.id)
                ?.amount?.getAmount() || 0,
            securityDeposit: summary.lineItems
              .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === productLineItem.id)
              ?.amount?.getAmount(),
            quantity: productLineItem.quantity,
            additionalFees: summary.lineItems
              .filter((line) => line.type === 'ProductFee' && line.productLineItemId === productLineItem.id)
              .map((line) => {
                const fee = line as ProductFeeLineItem;
                const orderProductFee = existingProductFees.find(
                  (f) => f.attributeId === fee.productFeeId && `${f.orderProductId}` === productLineItem.id,
                );

                return {
                  orderFeeId: orderProductFee?.id,
                  feeId: fee.productFeeId,
                  amount: fee.amount.getAmount(),
                  hasCustomAmount: fee.hasCustomAmount,
                };
              }),
            discount: op.discountPercentage,
            start: op.rentalStartDate,
            end:
              offRentRequest && offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id)
                ? offRentRequest.timestamp
                : op.rentalEndDate,
            customOrderRentalRates: customPrices.filter(
              (cp) => cp.productId === op.product.id && (cp.orderProductId == op.id || !cp.orderProductId),
            ),
            taxCode: order.taxCode,
            options: [],
            services: [],
            isCustom: false as const,
          };
        }
      }),
      ...novelProductLineItems,
    ],
  };
}

export function financialLineItemTypeToRank(
  type:
    | 'Product'
    | 'CustomProduct'
    | 'ProductFee'
    | 'Fee'
    | 'DamageWaiver'
    | 'AdditionalDiscount'
    | 'Coupon'
    | 'TagPricing'
    | 'ProductDiscount'
    | 'ProductDelivery'
    | 'ProductPickup'
    | 'ProductSecurityDeposit'
    | 'SecurityDeposit'
    | 'TaxLineItem'
    | 'Delivery'
    | 'Pickup'
    | 'Tax',
): number {
  return [
    'Product',
    'CustomProduct',
    'ProductFee',
    'Fee',
    'DamageWaiver',
    'AdditionalDiscount',
    'Coupon',
    'TagPricing',
    'ProductDiscount',
    'ProductDelivery',
    'ProductPickup',
    'ProductSecurityDeposit',
    'SecurityDeposit',
    'TaxLineItem',
    'Delivery',
    'Pickup',
    'Tax',
  ].indexOf(type);
}

export function isSameLine(a: LineItem, b: LineItem) {
  if (a.type !== b.type) {
    return false;
  }
  switch (a.type) {
    case 'Product': {
      const b1 = b as ProductLineItem;
      return a.id === b1.id;
    }
    case 'ProductFee': {
      const b1 = b as ProductFeeLineItem;
      return a.productFeeId === b1.productFeeId && a.productLineItemId === b1.productLineItemId;
    }
    case 'Fee': {
      const b1 = b as FeeLineItem;
      return a.feeId === b1.feeId;
    }
    case 'DamageWaiver':
    case 'AdditionalDiscount':
    case 'SecurityDeposit':
    case 'Delivery':
    case 'Pickup':
    case 'Tax':
    case 'Coupon':
    case 'TagPricing':
      return true;
  }

  return false;
}

export const getSaleItemRequestsFromFuelCharges = (
  fuelCharges: FuelCharge[],
  offRentRequest: OffRentRequest,
): SaleItemRequest[] => {
  return fuelCharges
    ? fuelCharges
        .filter(
          (fc) =>
            fc.fuelChargeProductId &&
            fc.amount > 0 &&
            offRentRequest.offRentProducts.map((orp) => orp.productId).includes(fc.orderProductId),
        )
        .map((fc) => ({
          id: fc.uuid,
          productId: fc.fuelChargeProductId!,
          quantity: fc.amount,
          type: OrderType.Sale,
        }))
    : [];
};

/*
  This function is used to update the item requests from the fuel charges.
  It removes the old (removed) fuel charges from the existing summary, updates the already-present ones,
  and adds the new fuel charges.
*/
export const getUpdatedItemRequestsFromFuelCharges = (
  fuelCharges: FuelCharge[],
  offRentRequest: OffRentRequest,
  existingSummaryRequest?: CalculateFinancialSummaryRequest,
) => {
  const mappedFuelRequests: SaleItemRequest[] = getSaleItemRequestsFromFuelCharges(fuelCharges, offRentRequest);

  const fuelRequestsAlreadyInSummary = mappedFuelRequests.filter((fc) =>
    existingSummaryRequest?.items.some((item) => item.id === fc.id),
  );

  const newFuelRequests = mappedFuelRequests.filter((fc) => !fuelRequestsAlreadyInSummary.includes(fc));

  const updatedItems =
    existingSummaryRequest?.items.map((item) => {
      const existingFuelCharge = mappedFuelRequests.find((fc) => fc.id === item.id);
      if (existingFuelCharge) {
        return {
          ...item,
          quantity: existingFuelCharge.quantity,
        };
      } else {
        return item;
      }
    }) ?? [];

  const updatedItemsWithOldFuelChargesRemoved = updatedItems.filter(
    (item) => !item.id.includes('fuel') || mappedFuelRequests.some((fc) => fc.id === item.id),
  );

  return [...updatedItemsWithOldFuelChargesRemoved, ...newFuelRequests];
};

export const shouldUpdateItemEndDate = (
  item: ItemRequest,
  offRentRequest: OffRentRequest,
  configs: Dictionary<Configuration> | undefined,
  order: Order,
  onOffRentUpdateDateCheckbox: boolean,
  onOffRentUpdateDateConfiguration: boolean,
) => {
  return (
    item.type === OrderType.Rental &&
    offRentRequest.offRentProducts.map((orp) => orp.productId).includes(parseInt(item.id)) &&
    configs &&
    configs['CONF_GRACE_PERIOD_FOR_RETURNS'] &&
    !order.isCycleBilled &&
    (!item.end ||
      item.end.plus(Duration.fromObject({ hours: parseFloat(configs['CONF_GRACE_PERIOD_FOR_RETURNS']?.value) })) <
        offRentRequest.timestamp ||
      (onOffRentUpdateDateCheckbox && onOffRentUpdateDateConfiguration))
  );
};
